// The draft course of the plot line (v2 §7, R2589; ui-map-interface.md
// §12.0 "The plot line"; mockup round 088 card 08).
//
// While the plot line stands, this layer draws the course the commander
// plots: a solid line through each leg, a ring at each leg, and a dashed
// line from the last leg of each loop part back to the point it opens at.
// The plot starts at its first leg (R2729): the joining run from the fleet
// to the first leg is no part of it, so it draws apart, dashed in the MOVE
// ink at `joinAlpha`, and no loop part opens at the fleet. A leg of a loop part takes the PATROL ink, because a loop
// course is the patrol of v2, and every other leg the MOVE ink. The line
// carries the width and the alpha of the drag order preview and the ring
// the radius of the crosshair gap, so the plot reads as an order given now.
// Every length takes `F_SCREEN`, so the course holds one size at every
// zoom.
//
// The cost follows `orderdrag.ts` (§10 of ui-map-interface.md): `set` with
// the draft it had packs nothing, because the plot line answers a new
// draft object on each edit and the same one otherwise, and a frame at an
// unchanged zoom re-flushes the last pack. A pack allocates nothing.
//
// The slot `plot` stands right after `orderdrag`. The layer reads no flag:
// it draws the draft the page hands it (§14.1 rule 5 of v2-the-deep.md).
//
// The lit course line (R3075, §6.1): a stretch of the draft where lit
// water or an armed lane light of the faction lifts the hull wears a band
// of the faction ink under its line (`FLEET_COURSE.litWidth`, `litAlpha`).
// `lit` answers the runs; the layer asks it at a pack alone.

import { FLEET_COURSE, FLEET_CROSSHAIR, FLEET_ORDER_COLORS, FLEET_ORDER_DRAG } from '../../gen/presentation';
import { F_SCREEN, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, SegmentSink } from './batches';

/** A point in world px. */
interface Pt { readonly x: number; readonly y: number }

/** What the layer draws: where the fleet stood when the plot line opened
 *  (the start of the joining run), and the legs of the plot. */
export interface PlotView {
  readonly from: Pt;
  readonly legs: readonly { readonly x: number; readonly y: number; readonly loop: boolean; readonly passes: number }[];
}

/** The ring of a leg is the crosshair gap: a point the commander placed. */
const RING_R = FLEET_CROSSHAIR.gapPx;

export class PlotLayer implements Layer {
  readonly name = 'plot';

  private readonly segments: SegmentSink;
  private readonly circles: CircleSink;
  private shown = true;
  private view: PlotView | null = null;
  private dirty = false;
  /** The zoom the packed instances were placed at. */
  private zoom = 0;
  /** How many times the layer packed. The tests read it. */
  packs = 0;
  /** The lit runs of a course from `from` through the points (x0, y0, x1,
   *  y1, world px) and the faction ink, or null on a page with no lanes
   *  (R3075). */
  lit: ((from: Pt, pts: readonly Pt[]) => { runs: readonly number[]; ink: number }) | null = null;

  constructor(src: BatchSource) {
    this.segments = src.newSegments(64);
    this.circles = src.newCircles(16);
  }

  /** The draft to draw, or null while no plot line stands. */
  set(view: PlotView | null): void {
    if (view === this.view) return;
    this.view = view;
    this.dirty = true;
  }

  update(frame: FrameInfo): void {
    const zoom = frame.cam.zoom;
    if (!this.dirty && zoom === this.zoom) return;
    this.dirty = false;
    this.zoom = zoom;
    this.pack();
  }

  draw(ctx: DrawCtx): void {
    this.segments.flush(ctx.cam);
    this.circles.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  private pack(): void {
    const s = this.segments;
    const c = this.circles;
    s.begin();
    c.begin();
    this.packs++;
    const v = this.view;
    if (!v || v.legs.length === 0) return;
    const d = FLEET_ORDER_DRAG;
    const move = FLEET_ORDER_COLORS.move.color, patrol = FLEET_ORDER_COLORS.patrol.color;
    // The lit bands first, so the line stands on them (R3075).
    const lit = this.lit?.(v.from, v.legs);
    if (lit) {
      const c = FLEET_COURSE, r = lit.runs;
      for (let i = 0; i + 3 < r.length; i += 4) s.line(r[i], r[i + 1], r[i + 2], r[i + 3], c.litWidth, lit.ink, c.litAlpha, F_SCREEN);
    }
    // The joining run, then the first leg, which starts the plot. Its ring
    // takes the ink of the leg after it: a loop part opens there.
    const first = v.legs[0];
    s.line(v.from.x, v.from.y, first.x, first.y, d.width, move, d.joinAlpha, F_SCREEN, d.dashOnPx, d.dashOffPx);
    c.ring(first.x, first.y, RING_R, d.width, v.legs[1]?.loop ? patrol : move, d.alpha, F_SCREEN);
    let ax = first.x, ay = first.y;
    // The leg the open loop part opens at.
    let ox = ax, oy = ay, inPart = false;
    for (let i = 1; i < v.legs.length; i++) {
      const l = v.legs[i];
      const ink = l.loop ? patrol : move;
      if (l.loop && !inPart) { inPart = true; ox = ax; oy = ay; }
      s.line(ax, ay, l.x, l.y, d.width, ink, d.alpha, F_SCREEN);
      c.ring(l.x, l.y, RING_R, d.width, ink, d.alpha, F_SCREEN);
      const next = v.legs[i + 1];
      // The part ends at a leg with a pass count, before a leg of no
      // part, or at the last leg: the fleet sails back to its opening point.
      if (l.loop && (l.passes > 0 || !next || !next.loop)) {
        s.line(l.x, l.y, ox, oy, d.width, patrol, d.alpha, F_SCREEN, d.dashOnPx, d.dashOffPx);
        inPart = false;
      }
      if (!l.loop) inPart = false;
      ax = l.x; ay = l.y;
    }
  }
}
